import {Component, forwardRef, Input, OnInit} from '@angular/core';
import {ClientConfiguration} from '@twpub/core/utils';
import {NGXLogger} from 'ngx-logger';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FontFileService} from '@twpub/core/services/font-file.service';
import {FontFile} from '@twpub/core/models/font-file';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';

@Component({
  selector: 'pub-font-upload',
  templateUrl: './font-upload.component.html',
  styleUrls: ['./font-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FontUploadComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [MatIconButton, MatIcon, MatButton]
})
export class FontUploadComponent implements OnInit, ControlValueAccessor {
  @Input() editConfiguration!: ClientConfiguration;

  selectedFile: File | null = null;
  isUploading = false;
  uploadProgress = 0;
  uploadedFonts: FontFile[] = [];
  defaultFontWeight = 'normal';
  defaultFontStyle = 'normal';

  private onChange: (value: number[]) => void = () => {
  };

  private onTouched: () => void = () => {
  };

  constructor(private logger: NGXLogger, private snackBar: MatSnackBar,
              private fontService: FontFileService) {
  }

  ngOnInit(): void {
    this.logger.trace('FontUploadComponent.ngOnInit:', {editConfiguration: this.editConfiguration});
  }

  /**
   * Fetch font details from the server
   */
  private fetchFontDetails(fontId: number): void {
    this.fontService.getFontFile(fontId).subscribe({
      next: (font) => {
        this.logger.trace('FontUploadComponent.fetched font detail:', {fontId})
        this.uploadedFonts.push(font);
      },
      error: (err) => {
        this.logger.error(`Failed to fetch font with ID ${fontId}`, err);
        // Remove invalid font ID from configuration
        if (this.editConfiguration?.fontIds) {
          this.editConfiguration.fontIds = this.editConfiguration.fontIds.filter(id => id !== fontId);
          this.updateFontIds();
        }
      }
    });
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (element.files?.length) {
      this.selectedFile = element.files[0];
    }
  }

  /**
   * Upload the selected font file
   */
  uploadFont(): void {
    if (!this.selectedFile) {
      this.snackBar.open('Please select a font file first', 'Close', {duration: 3000});
      return;
    }

    if (!this.isSupportedFontType(this.selectedFile.type)) {
      this.snackBar.open('Unsupported font format. Please use WOFF, WOFF2, TTF, or OTF.', 'Close', {duration: 3000});
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;

    const formData = new FormData();
    formData.append('file', this.selectedFile);

    // Try to extract font family from filename if not provided
    const fontFamily = this.extractFontFamilyFromFilename(this.selectedFile.name);
    formData.append('fontFamily', fontFamily);
    formData.append('fontWeight', this.defaultFontWeight);
    formData.append('fontStyle', this.defaultFontStyle);

    this.fontService.saveFile(this.selectedFile).subscribe({
      next: (uploadedFont: FontFile) => {
        // Add the font ID to the configuration
        if (!this.editConfiguration.fontIds) {
          this.editConfiguration.fontIds = [];
        }

        if (uploadedFont.id && !this.editConfiguration.fontIds.includes(uploadedFont.id)) {
          this.uploadedFonts.push(uploadedFont);
          this.editConfiguration.fontIds.push(uploadedFont.id);
          this.logger.trace('FontUploadComponent: pushed to editconf', {id: uploadedFont.id})
          this.updateFontIds();
        }

        this.isUploading = false;
        this.selectedFile = null;
        this.snackBar.open('Font uploaded successfully', 'Close', {duration: 3000});

        // Reset the file input
        const fileInput = document.getElementById('fontFileInput') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      },
      error: (error) => {
        this.logger.error('Error uploading font', error);
        this.isUploading = false;
        this.snackBar.open('Failed to upload font file', 'Close', {duration: 3000});
      }
    });
  }

  /**
   * Remove a font from the configuration
   */
  removeFont(font: FontFile): void {
    if (font.id && this.editConfiguration?.fontIds) {
      this.editConfiguration.fontIds = this.editConfiguration.fontIds.filter(id => id !== font.id);
      this.updateFontIds();
    }

    this.uploadedFonts = this.uploadedFonts.filter(f => f.id !== font.id);
  }

  /**
   * Update the configuration and notify form control of value change
   */
  private updateFontIds(): void {
    if (this.editConfiguration?.fontIds) {
      this.onChange(this.editConfiguration.fontIds);
    }
  }

  /**
   * Check if the file type is a supported font type
   */
  private isSupportedFontType(mimeType: string): boolean {
    const supportedTypes = [
      'font/woff', 'font/woff2', 'font/ttf', 'font/otf',
      'application/font-woff', 'application/font-woff2',
      'application/x-font-ttf', 'application/x-font-opentype',
      'application/octet-stream' // Fallback for some browsers
    ];
    return supportedTypes.includes(mimeType);
  }

  /**
   * Extract a font family name from the filename
   */
  private extractFontFamilyFromFilename(filename: string): string {
    if (!filename) return 'Unknown Font';

    // Remove file extension
    let nameWithoutExtension = filename;
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex > 0) {
      nameWithoutExtension = filename.substring(0, lastDotIndex);
    }

    // Replace hyphens and underscores with spaces
    let name = nameWithoutExtension.replace(/-/g, ' ').replace(/_/g, ' ');

    // Remove common suffixes
    name = name.replace(/\b(regular|medium|light|bold|italic|oblique|thin|black|heavy)\b/i, '').trim();

    return name || 'Unknown Font';
  }

  writeValue(fontIds: number[]): void {
    if (fontIds && Array.isArray(fontIds)) {
      // Clear existing fonts first to avoid duplicates
      this.uploadedFonts = [];
      this.logger.debug('FontUploadComponent.writeValue: received fontIds', fontIds);
      fontIds.forEach(fontId => this.fetchFontDetails(fontId));

      if (this.editConfiguration) {
        this.editConfiguration.fontIds = [...fontIds];
      }
    } else {
      this.uploadedFonts = [];
      if (this.editConfiguration) {
        this.editConfiguration.fontIds = [];
      }
    }
  }

  registerOnChange(fn: (value: number[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implementation for disabled state if needed
  }
}
